/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Aug 30 2023
 **/

import { useEffect, useRef, useState } from "react";

interface useGridProps {
	width: number;
	gap: number;
}

/**
 * @param width - 그리드 개별 아이템의 너비값
 * @param gap - 그리드 요소간의 간격
 *
 * @return targetRef - 대상 그리드 레이아웃
 * @return column - column 개수
 *
 * @example
 * ```tsx
 * import { Grid } from "designStuff/Components"
 * import { useGridResize } from "@/frameworks/libraries/hooks"
 *
 * function GridComp() {
 *   const { targetRef, column } = useGridResize({ width: 240, gap: 12 });
 *
 *   return (
 * 		<Grid ref={targetRef} column={column} gap={12}>
 * 			<div css={{ width: "240px" }}/>
 * 			<div css={{ width: "240px" }}/>
 *   	</Grid>
 * 	 )
 * }
 * ```
 */
export default function useGridResize<E extends HTMLElement>({ width, gap = 0 }: useGridProps) {
	const ref = useRef<E>(null);
	const [column, setColumn] = useState(0);

	useEffect(() => {
		if (!ref.current) return;
		const { clientWidth } = ref.current;
		setColumn(Math.floor((clientWidth + gap * 2) / (width + gap)));

		const observer = new ResizeObserver((entries) => {
			setColumn(Math.floor((entries[0].target.clientWidth + gap * 2) / (width + gap)));
		});

		observer.observe(ref.current);
		return () => {
			observer.disconnect();
		};
	}, []);

	return { targetRef: ref, column };
}
